import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { userRewardApi } from '../../api/reward.api';
import type {
  RewardStatusResponse,
  PointBalanceResponse,
  BonusResponse,
  MyCreatorMilestoneItem,
  CreatorMilestoneResponse,
  RewardHistoryResponse,
  RewardHistoryItem,
  LeaderboardRewardItem,
} from '../../types/Reward.api.type';
import { Button } from '../ui/button';
import { POINT_NAME, POINT_UNIT, formatPoint } from '../../constants/point';
import ExchangeBottomSheet from './ExchangeBottomSheet';

/** 지급 타입 한글 라벨 */
const getRewardTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    VOTE: '투표 보너스',
    VOTE_AD_BONUS: '광고 보너스 (투표)',
    DAILY_BONUS: '일일 마일스톤',
    DAILY_AD_BONUS: '광고 보너스 (마일스톤)',
    WEEKLY_MILESTONE_1: '주간 마일스톤 1단계',
    WEEKLY_MILESTONE_2: '주간 마일스톤 2단계',
    WEEKLY_MILESTONE_3: '주간 마일스톤 3단계',
    MILESTONE_AD_BONUS: '광고 보너스 (꺼내기)',
    CREATOR_MILESTONE: '설문 작성 보너스',
    FIRST_VOTE_BONUS: '첫 투표 보너스',
    ATTENDANCE: '출석 체크',
    ATTENDANCE_AD_BONUS: '광고 보너스 (출석)',
    ATTENDANCE_STREAK: '연속 출석 보너스',
    ATTENDANCE_STREAK_SAVE: '연속 유지',
    COMMENT: '댓글 보너스',
    COMMENT_AD_BONUS: '광고 보너스 (댓글)',
    SURVEY_LIKE: '설문 좋아요',
    SURVEY_LIKE_AD_BONUS: '광고 보너스 (좋아요)',
    COMMENT_LIKE: '댓글 좋아요',
    COMMENT_LIKE_AD_BONUS: '광고 보너스 (댓글 좋아요)',
    EXCHANGE: '꺼내기',
    LEADERBOARD: '랭킹 보상',
  };
  return labels[type] || type;
};

const RewardTab = () => {
  const queryClient = useQueryClient();

  /** 꺼내기 모달 */
  const [exchangeOpen, setExchangeOpen] = useState(false);

  /** 보상 수령 후 모달 제어 */
  const [milestoneModal, setMilestoneModal] = useState<{
    open: boolean;
    type: 'daily' | 'weekly';
    milestone: number;
    amount: number;
  }>({ open: false, type: 'weekly', milestone: 0, amount: 0 });

  /** 포인트 잔액 조회 */
  const { data: pointBalance } = useQuery<PointBalanceResponse>({
    queryKey: ['pointBalance'],
    queryFn: () => userRewardApi.getPointBalance().then((res) => res.data.results),
  });

  /** 리워드 현황 조회 */
  const { data: status } = useQuery<RewardStatusResponse>({
    queryKey: ['rewardStatus'],
    queryFn: () => userRewardApi.getStatus().then((res) => res.data.results),
  });

  /** S6-13: 지급 내역 히스토리 */
  const { data: history } = useQuery<RewardHistoryResponse>({
    queryKey: ['rewardHistory'],
    queryFn: () => userRewardApi.getHistory(0, 20).then((res) => res.data.results),
  });

  /** S6-27: 리더보드 미수령 보상 */
  const { data: leaderboardRewards } = useQuery<LeaderboardRewardItem[]>({
    queryKey: ['leaderboardRewards'],
    queryFn: () => userRewardApi.getLeaderboardRewards().then((res) => res.data),
  });

  /** S6-27: 리더보드 보상 수령 */
  const { mutate: claimLeaderboardReward, isPending: isLeaderboardClaimPending } = useMutation({
    mutationFn: (snapshotId: number) => userRewardApi.claimLeaderboardReward(snapshotId),
    onSuccess: () => {
      toast.success('랭킹 보상을 받았어요!');
      queryClient.invalidateQueries({ queryKey: ['leaderboardRewards'] });
      queryClient.invalidateQueries({ queryKey: ['pointBalance'] });
      queryClient.invalidateQueries({ queryKey: ['rewardHistory'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || '보상 수령에 실패했어요');
    },
  });

  /** 내가 작성한 설문 마일스톤 현황 */
  const { data: myCreatorMilestones } = useQuery<MyCreatorMilestoneItem[]>({
    queryKey: ['myCreatorMilestones'],
    queryFn: () => userRewardApi.getMyCreatorMilestones().then((res) => res.data.results),
  });

  /** 작성자 마일스톤 수동 수령 */
  const { mutate: claimCreatorMilestone, isPending: isCreatorClaimPending } = useMutation({
    mutationFn: (questionId: number) => userRewardApi.claimCreatorMilestone(questionId),
    onSuccess: (res) => {
      const data = res.data.results as CreatorMilestoneResponse;
      const newlyClaimed = data.milestones.filter((m) => m.claimed);
      if (newlyClaimed.length > 0) {
        const totalReward = newlyClaimed.reduce((sum, m) => sum + m.rewardAmount, 0);
        toast.success(formatPoint(totalReward) + '를 받았어요!');
      }
      queryClient.invalidateQueries({ queryKey: ['myCreatorMilestones'] });
      queryClient.invalidateQueries({ queryKey: ['pointBalance'] });
      queryClient.invalidateQueries({ queryKey: ['rewardHistory'] });
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message || '마일스톤 수령에 실패했어요.';
      toast.error(message);
    },
  });

  /** 일일 보너스 수령 */
  const { mutate: claimDailyBonus, isPending: isDailyBonusPending } = useMutation({
    mutationFn: () => userRewardApi.claimDailyBonus(),
    onSuccess: (res) => {
      const data = res.data.results as BonusResponse;
      if (data.rewarded) {
        setMilestoneModal({
          open: true,
          type: 'daily',
          milestone: data.milestone ?? 0,
          amount: data.amount,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['rewardStatus'] });
      queryClient.invalidateQueries({ queryKey: ['pointBalance'] });
      queryClient.invalidateQueries({ queryKey: ['rewardHistory'] });
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message || '보너스 수령에 실패했어요.';
      toast.error(message);
    },
  });

  /** 주간 마일스톤 수령 */
  const { mutate: claimMilestone, isPending: isMilestonePending } = useMutation({
    mutationFn: (milestone: number) => userRewardApi.claimWeeklyMilestone(milestone),
    onSuccess: (res) => {
      const data = res.data.results as BonusResponse;
      if (data.rewarded && data.milestone) {
        setMilestoneModal({
          open: true,
          type: 'weekly',
          milestone: data.milestone,
          amount: data.amount,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['rewardStatus'] });
      queryClient.invalidateQueries({ queryKey: ['pointBalance'] });
      queryClient.invalidateQueries({ queryKey: ['rewardHistory'] });
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message || '마일스톤 수령에 실패했어요.';
      toast.error(message);
    },
  });

  if (!status) return null;

  const dailyCount = status.dailyVoteCount ?? 0;
  const dailyBonuses = status.dailyBonuses ?? [];
  const nextDailyStage = dailyBonuses.find((b) => !b.claimed);
  const nextDailyThreshold = nextDailyStage?.threshold ?? (dailyBonuses.length > 0 ? dailyBonuses[dailyBonuses.length - 1].threshold : 10);
  const dailyProgressRatio = nextDailyThreshold > 0 ? dailyCount / nextDailyThreshold : 0;

  const weeklyProgress = status.weeklyProgress ?? 0;
  const milestones = status.weeklyMilestones ?? [];
  const nextWeeklyMs = milestones.find((m) => !m.claimed);
  const nextWeeklyThreshold = nextWeeklyMs?.threshold ?? (milestones.length > 0 ? milestones[milestones.length - 1].threshold : 10);
  const weeklyProgressRatio = nextWeeklyThreshold > 0 ? weeklyProgress / nextWeeklyThreshold : 0;

  return (
    <div className="flex flex-col gap-6">
      {/* 저금통 */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 flex items-center justify-between">
        <div>
          <span className="text-sm text-gray-500">지금까지 모은 {POINT_NAME}</span>
          <div className="mt-1">
            <span className="text-2xl font-bold text-gray-900">
              {pointBalance?.balance ?? 0}{POINT_UNIT}
            </span>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setExchangeOpen(true)}
        >
          꺼내기
        </Button>
      </div>

      {/* S6-27: 리더보드 미수령 보상 */}
      {leaderboardRewards && leaderboardRewards.length > 0 && (
        <div className="space-y-2">
          {leaderboardRewards.filter((r) => !r.claimed).map((reward) => (
            <div
              key={reward.snapshotId}
              className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between"
            >
              <div>
                <p className="text-sm font-bold text-gray-900">
                  🏆 주간 랭킹 {reward.rank}위 달성!
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {reward.periodStart} ~ {reward.periodEnd}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => claimLeaderboardReward(reward.snapshotId)}
                disabled={isLeaderboardClaimPending}
              >
                {reward.rewardAmount}조각 받기
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* 오늘의 투표 */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-gray-900">오늘의 투표</span>
          <span className="text-sm text-gray-500">{dailyCount}/{nextDailyThreshold}회</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(dailyProgressRatio * 100, 100)}%` }}
              />
            </div>
          </div>
          {(() => {
            const allClaimed = dailyBonuses.length > 0 && dailyBonuses.every((b) => b.claimed);
            if (allClaimed) {
              return (
                <Button size="sm" variant="secondary" disabled>
                  완료
                </Button>
              );
            }
            const reached = nextDailyStage && dailyCount >= nextDailyStage.threshold;
            return reached ? (
              <Button
                size="sm"
                onClick={() => claimDailyBonus()}
                disabled={isDailyBonusPending}
              >
                받기
              </Button>
            ) : (
              <Button size="sm" variant="secondary" disabled>
                받기
              </Button>
            );
          })()}
        </div>
      </div>

      {/* 이번주 투표 (주간 마일스톤) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-bold text-gray-900">이번주 투표</span>
            <p className="text-xs text-gray-400 mt-0.5">일주일마다 횟수가 초기화돼요 (1일 5회 제한)</p>
          </div>
          <span className="text-sm text-gray-500">{weeklyProgress}/{nextWeeklyThreshold}회</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(weeklyProgressRatio * 100, 100)}%` }}
              />
            </div>
          </div>
          {(() => {
            const allClaimed = milestones.length > 0 && milestones.every((m) => m.claimed);
            if (allClaimed) {
              return (
                <Button size="sm" variant="secondary" disabled>
                  완료
                </Button>
              );
            }
            const reached = nextWeeklyMs && weeklyProgress >= nextWeeklyMs.threshold;
            return reached ? (
              <Button
                size="sm"
                onClick={() => nextWeeklyMs && claimMilestone(nextWeeklyMs.milestone)}
                disabled={isMilestonePending}
              >
                받기
              </Button>
            ) : (
              <Button size="sm" variant="secondary" disabled>
                받기
              </Button>
            );
          })()}
        </div>
      </div>

      {/* 내가 작성한 설문 */}
      {myCreatorMilestones && myCreatorMilestones.length > 0 && (
        <div className="space-y-3">
          <span className="text-sm text-gray-500">내가 작성한 설문 목록이에요.</span>
          {myCreatorMilestones.map((item) => {
            const nextMilestone = item.milestones.find((m) => !m.claimed);
            const allClaimed = item.milestones.length > 0 && item.milestones.every((m) => m.claimed);
            const nextThreshold = nextMilestone?.threshold
              ?? (item.milestones.length > 0 ? item.milestones[item.milestones.length - 1].threshold : 10);
            const progressRatio = nextThreshold > 0 ? item.participantCount / nextThreshold : 0;
            const reached = nextMilestone && item.participantCount >= nextMilestone.threshold;

            return (
              <div key={item.questionId}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-gray-700 truncate flex-1 mr-2">{item.questionTitle}</span>
                  <span className="text-xs text-gray-500 flex-shrink-0">{item.participantCount}/{nextThreshold}명</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(progressRatio * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  {allClaimed ? (
                    <Button size="sm" variant="secondary" disabled>완료</Button>
                  ) : reached ? (
                    <Button
                      size="sm"
                      onClick={() => claimCreatorMilestone(item.questionId)}
                      disabled={isCreatorClaimPending}
                    >
                      받기
                    </Button>
                  ) : (
                    <Button size="sm" variant="secondary" disabled>받기</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* S6-13: 지급 내역 히스토리 */}
      <div className="space-y-3">
        <span className="text-sm font-bold text-gray-900">지급 내역</span>
        {history?.content && history.content.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {history.content.map((item: RewardHistoryItem) => (
              <div key={item.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">{getRewardTypeLabel(item.rewardType)}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {new Date(item.createdAt).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <span className={`text-sm font-bold ${item.amount < 0 ? 'text-red-500' : 'text-blue-600'}`}>
                  {item.amount < 0 ? `${item.amount}조각` : `+${item.amount}조각`}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center">
            <p className="text-sm text-gray-500">아직 활동 내역이 없어요</p>
          </div>
        )}
      </div>

      {/* 안내사항 */}
      <div className="mt-4 bg-gray-50 rounded-lg p-4">
        <ul className="text-xs text-gray-500 space-y-1.5 list-disc list-inside">
          <li>투표에 참여하면 {POINT_NAME}이 적립돼요. (1일 최대 5회)</li>
          <li>모은 {POINT_NAME}은 토스 앱에서 토스포인트로 바꿀 수 있어요.</li>
          <li>내 설문에 참여자가 늘어나면 보너스 {POINT_NAME}을 받아요.</li>
          <li>설문 종료 시 작성자 {POINT_NAME} 지급도 종료돼요.</li>
          <li>웹에서는 {POINT_NAME} 획득만 가능하고, 토스 포인트로 바꾸기는 토스 앱에서 할 수 있어요.</li>
          <li>부정 이용이 확인되면 {POINT_NAME}이 회수될 수 있어요.</li>
        </ul>
      </div>

      {/* 마일스톤 수령 모달 (Web: 광고 없이 확인만) */}
      {milestoneModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 w-80 text-center shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {milestoneModal.type === 'daily' ? '오늘의 투표' : '이번주 투표'}
            </h3>
            {milestoneModal.amount > 0 ? (
              <p className="text-xl font-bold text-blue-600 mb-4">
                {formatPoint(milestoneModal.amount)}를 받았어요!
              </p>
            ) : (
              <p className="text-gray-600 mb-4">보상을 받았어요!</p>
            )}
            <Button
              className="w-full"
              onClick={() => setMilestoneModal((prev) => ({ ...prev, open: false }))}
            >
              확인
            </Button>
          </div>
        </div>
      )}

      {/* 꺼내기 모달 */}
      <ExchangeBottomSheet
        isOpen={exchangeOpen}
        onClose={() => setExchangeOpen(false)}
        balance={pointBalance?.balance ?? 0}
      />
    </div>
  );
};

export default RewardTab;
